import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const BiasAnalysisForm = ({ onAnalyze, loading }) => {
  const [inputType, setInputType] = useState('text');
  const [textContent, setTextContent] = useState('');
  const [urlContent, setUrlContent] = useState('');
  const [validationError, setValidationError] = useState('');

  // Auto-detect input type based on content
  useEffect(() => {
    const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
    
    if (textContent?.trim()) {
      if (urlPattern?.test(textContent?.trim())) {
        setInputType('url');
        setUrlContent(textContent?.trim());
        setTextContent('');
      } else {
        setInputType('text');
      }
    }
  }, [textContent]);

  const handleInputTypeChange = (type) => {
    setInputType(type);
    setValidationError('');
    if (type === 'text') {
      setUrlContent('');
    } else {
      setTextContent('');
    }
  };

  const validateInput = () => {
    if (inputType === 'text') {
      if (!textContent?.trim()) {
        setValidationError('Please enter text content to analyze');
        return false;
      }
      if (textContent?.trim()?.length < 50) {
        setValidationError('Text content should be at least 50 characters for meaningful analysis');
        return false;
      }
    } else {
      if (!urlContent?.trim()) {
        setValidationError('Please enter a URL to analyze');
        return false;
      }
      const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
      if (!urlPattern?.test(urlContent?.trim())) {
        setValidationError('Please enter a valid URL');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    setValidationError('');
    
    if (validateInput()) {
      const analysisData = {
        type: inputType,
        content: inputType === 'text' ? textContent?.trim() : urlContent?.trim()
      };
      onAnalyze(analysisData);
    }
  };

  return (
    <div className="bg-card rounded-xl shadow-elevated border border-border p-8">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-gradient-primary rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Icon name="Search" size={32} color="white" />
        </div>
        <h2 className="text-2xl font-heading font-bold text-foreground mb-2">
          Bias Detection Analysis
        </h2>
        <p className="text-muted-foreground">
          Analyze content for potential bias patterns and receive actionable improvement recommendations
        </p>
      </div>
      {/* Input Type Toggle */}
      <div className="flex bg-muted rounded-lg p-1 mb-6">
        <button
          type="button"
          onClick={() => handleInputTypeChange('text')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all duration-250 flex items-center justify-center space-x-2 ${
            inputType === 'text' ?'bg-white text-foreground shadow-soft' :'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Icon name="FileText" size={16} />
          <span>Text Content</span>
        </button>
        <button
          type="button"
          onClick={() => handleInputTypeChange('url')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all duration-250 flex items-center justify-center space-x-2 ${
            inputType === 'url' ?'bg-white text-foreground shadow-soft' :'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Icon name="Link" size={16} />
          <span>URL Analysis</span>
        </button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        {inputType === 'text' ? (
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Article Text
            </label>
            <textarea
              value={textContent}
              onChange={(e) => setTextContent(e?.target?.value)}
              placeholder="Paste your article content here for bias analysis..."
              className="w-full h-48 px-4 py-3 border border-border rounded-lg bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
              disabled={loading}
            />
            <p className="text-xs text-muted-foreground mt-2">
              Minimum 50 characters required for analysis
            </p>
          </div>
        ) : (
          <Input
            label="Article URL"
            type="url"
            placeholder="https://example.com/article"
            value={urlContent}
            onChange={(e) => setUrlContent(e?.target?.value)}
            description="Enter the URL of the article you want to analyze for bias"
            disabled={loading}
          />
        )}

        {validationError && (
          <div className="p-4 bg-error/10 border border-error/20 rounded-lg flex items-center space-x-3">
            <Icon name="AlertCircle" size={20} color="var(--color-error)" />
            <p className="text-sm text-error font-medium">{validationError}</p>
          </div>
        )}

        <Button
          type="submit"
          variant="default"
          size="lg"
          fullWidth
          loading={loading}
          disabled={loading}
          className="bg-gradient-primary hover:opacity-90 transition-opacity duration-250"
          iconName="Search"
          iconPosition="left"
        >
          {loading ? 'Analyzing Content...' : 'Analyze Bias'}
        </Button>
      </form>
      {/* Analysis Tips */}
      <div className="mt-8 p-4 bg-muted/50 rounded-lg">
        <h3 className="text-sm font-medium text-foreground mb-2 flex items-center space-x-2">
          <Icon name="Lightbulb" size={16} color="var(--color-primary)" />
          <span>Analysis Tips</span>
        </h3>
        <ul className="text-xs text-muted-foreground space-y-1">
          <li>• Longer content provides more accurate bias detection</li>
          <li>• URL analysis includes headline and article body content</li>
          <li>• Results include specific examples and improvement suggestions</li>
          <li>• Analysis covers political, cultural, and linguistic bias patterns</li>
        </ul>
      </div>
    </div>
  );
};

export default BiasAnalysisForm;